import { useLingui } from '@lingui/react/macro'
import { useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { MenuIcon } from 'lucide-react'
import { LogoIcon as Logo } from '@/routes/_app/-components/shared/logo'
import { ThemeButton } from '@/routes/_landing/-components/theme-button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useAppStore } from '@/lib/app-store'
import { cn } from '@/lib/utils'

export function Header(props: { aboveBackground?: boolean; className?: string }) {
  const { t } = useLingui()
  const { store } = useAppStore()
  const [isScrolled, setIsScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = () => setMenuOpen(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 400
      if (scrolled !== isScrolled) setIsScrolled(scrolled)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isScrolled])

  const shouldShowLightText = props.aboveBackground && !isScrolled
  // Over the colored background → always white. Otherwise follow the theme so
  // the toggle is light-on-dark in dark mode and dark-on-light in light mode.
  const themeText = store.resolvedDarkMode === 'dark' ? '255 255 255' : '0 0 0'

  return (
    <header
      className={cn(
        !shouldShowLightText &&
          'shadow-3xl bg-background sticky top-0 z-20 dark:shadow-[0px_8px_24px_0px_rgb(0_0_0/0.2)]',
        props.className,
      )}
    >
      <div className="mx-auto flex w-full max-w-270 items-center px-4 py-0">
        <Link to="/" className="p-3 md:p-5">
          <Logo
            className="h-auto w-32 md:w-41"
            style={
              shouldShowLightText ? { '--color-primary': '255 255 255 / 0.7', '--text': '255 255 255' } : undefined
            }
          />
        </Link>

        <Link
          to="/professionals"
          className={cn(
            'hidden px-3.5 py-2.5 text-[16px] font-medium transition-opacity hover:opacity-70 md:block',
            shouldShowLightText
              ? 'text-white/80 aria-[current=page]:text-white'
              : 'text-foreground/80 aria-[current=page]:text-foreground',
          )}
        >
          {t`Professionals`}
        </Link>

        <Link
          to="/docs"
          className={cn(
            'hidden px-3.5 py-2.5 text-[16px] font-medium transition-opacity hover:opacity-70 md:block',
            shouldShowLightText
              ? 'text-white/80 aria-[current=page]:text-white'
              : 'text-foreground/80 aria-[current=page]:text-foreground',
          )}
        >
          {t`Documentation`}
        </Link>

        <ThemeButton className="ml-auto" style={{ '--text': shouldShowLightText ? '255 255 255' : themeText }} />

        <Link
          to="/register"
          className={cn(
            'hidden px-3.5 py-2.5 text-[16px] font-medium transition-opacity hover:opacity-70 md:block',
            shouldShowLightText ? 'text-white' : 'text-foreground',
          )}
        >
          {t`Sign up`}
        </Link>

        <Link
          to="/login"
          className={cn(
            'hidden px-3.5 py-2.5 text-[16px] font-medium transition-opacity hover:opacity-70 md:block',
            shouldShowLightText ? 'text-white' : 'text-foreground',
          )}
        >
          {t`Sign in`}
        </Link>

        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger
            aria-label={t`Open menu`}
            className={cn(
              'cursor-pointer rounded-[5px] p-2.5 transition-opacity hover:opacity-70 md:hidden',
              shouldShowLightText ? 'text-white' : 'text-foreground',
            )}
          >
            <MenuIcon className="size-6" />
          </SheetTrigger>
          <SheetContent side="right" className="w-72">
            <SheetHeader>
              <SheetTitle>
                <Logo />
              </SheetTitle>
            </SheetHeader>
            {/*
              The sheet is already controlled, so each entry closes it from its own `onClick` instead of
              going through `SheetClose render={<Link/>}`: SheetClose is a base-ui Dialog.Close, which
              asserts it rendered a native <button> and would otherwise have to be told `nativeButton={false}`
              — and that stamps `role="button"` over the anchor, costing these nav entries their link role.
            */}
            <nav className="flex flex-col px-2">
              <Link
                to="/professionals"
                onClick={closeMenu}
                className="text-foreground/80 aria-[current=page]:text-foreground rounded-md px-3.5 py-3 text-[16px] font-medium transition-opacity hover:opacity-70"
              >
                {t`Professionals`}
              </Link>
              <Link
                to="/docs"
                onClick={closeMenu}
                className="text-foreground/80 aria-[current=page]:text-foreground rounded-md px-3.5 py-3 text-[16px] font-medium transition-opacity hover:opacity-70"
              >
                {t`Documentation`}
              </Link>
              <Link
                to="/register"
                onClick={closeMenu}
                className="text-foreground rounded-md px-3.5 py-3 text-[16px] font-medium transition-opacity hover:opacity-70"
              >
                {t`Sign up`}
              </Link>
              <Link
                to="/login"
                onClick={closeMenu}
                className="text-foreground rounded-md px-3.5 py-3 text-[16px] font-medium transition-opacity hover:opacity-70"
              >
                {t`Sign in`}
              </Link>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
